import { Button, Icon, ThemeColor } from "@deligoo/ui"
import { IconName } from "@deligoo/ui/src/icons/build"
import clsx from "clsx"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"
import { useHistory } from "react-router-dom"

import { useAppSelector } from "@/App"
import { Main } from "@/components/AppLayout"
import { NotificationsList } from "@/components/NotificationsList"
import { Status } from "@/components/Status"
import { selectNotifications, selectNotificationsStatus } from "@/store/notifications"
import { selectCurrentTeam } from "@/store/teams"
import { selectCurrentTeamStats, selectTeamsStatsStatus } from "@/store/teamsStats"
import { TeamStats } from "@/types"
import { getStatsColor } from "@/utils"

import { handleDriversCountClick, handleUnsettledPaymentsClick } from "../utils"
import cls from "./EventsTiles.module.scss"

export const EventsTiles = () => {
  const team = useAppSelector(selectCurrentTeam, shallowEqual)
  const notifications = useAppSelector(team?.id ? selectNotifications([team.id]) : () => undefined, shallowEqual) // TODO
  const notificationsStatus = useAppSelector(selectNotificationsStatus)
  const teamStats = useAppSelector(selectCurrentTeamStats, shallowEqual)
  const teamStatsStatus = useAppSelector(selectTeamsStatsStatus)

  const history = useHistory()

  const { t } = useTranslation()

  const [areSectionsHidden, setHiddenSections] = useState(false)

  const toggleHiddenSections = useCallback(() => setHiddenSections((currentValue) => !currentValue), [])

  if (!team) return <Status type="no_team" title={t("events_view")} fullHeight />

  if (!teamStats || teamStatsStatus === "loading" || teamStatsStatus === "error") {
    return <Status type={teamStatsStatus} title={t("events_view")} fullHeight />
  }

  const {
    total_drivers_count,
    active_drivers_count,
    unsettled_payments_count,
    today_orders_count,
    today_delivery_orders_count,
    load_value,
    blocked_drivers_count,
    late_drivers_count,
  } = teamStats

  return (
    <Main wrapperClassName={cls.eventsTilesPage} page>
      <nav className={cls.nav}>
        <div className={cls.controls}>
          <span className={cls.viewType}>{t("view_type")}:</span>

          <Button
            color="primary"
            size="large"
            iconSide="right"
            iconProps={{ name: "grid" }}
            className={clsx(cls.navButton, cls.active, "hide-mobile")}
            onClick={() => history.push("/centrum-zdarzen")}
            pill
          >
            {t("grid")}
          </Button>

          <Button
            color="dark"
            size="large"
            iconSide="right"
            iconProps={{ name: "statistics", color: "primary" }}
            className={cls.navButton}
            onClick={() => history.push("/centrum-zdarzen/statystyki")}
            pill
          >
            {t("statistics")}
          </Button>

          <Button
            color="dark"
            size="large"
            iconSide="right"
            iconProps={{ name: "list-3", color: "primary" }}
            className={cls.navButton}
            onClick={() => history.push("/centrum-zdarzen/lista")}
            pill
          >
            {t("list")}
          </Button>
        </div>
      </nav>

      {!areSectionsHidden && (
        <div className={cls.stats}>
          <StatItem
            icon="courier"
            label={t(`stats.active_drivers_count`)}
            value={active_drivers_count}
            valueColor={getStatsColor(teamStats, "active_drivers_count")}
            valueTotal={total_drivers_count}
            onMouseDown={(event) => handleDriversCountClick("inactive", team, event, history)}
          />

          <StatItem
            icon="no-credit-card"
            label={t(`stats.unsettled_payments_count`)}
            valueColor={getStatsColor(teamStats, "unsettled_payments_count")}
            value={unsettled_payments_count}
            onMouseDown={(event) => handleUnsettledPaymentsClick(team, event, history)}
          />

          <StatItem
            icon="boxes"
            label={t(`stats.orders`)}
            value={
              <>
                <span className={`text-${getStatsColor(teamStats, "today_orders_count")}`}>{today_orders_count}</span>
                <span className={cls.separator} style={{ margin: "0 .5rem" }}>
                  /
                </span>
                <span className={`text-${getStatsColor(teamStats, "load_value")}`}>{load_value?.toFixed(2)}</span>
              </>
            }
          />

          <StatItem
            icon="delivery"
            label={t(`stats.today_delivery_orders_count`)}
            value={today_delivery_orders_count}
            valueColor={getStatsColor(teamStats, "today_delivery_orders_count")}
          />

          <StatItem
            icon="stoper"
            label={t(`stats.blocked_drivers_count`)}
            value={blocked_drivers_count}
            valueColor={getStatsColor(teamStats, "blocked_drivers_count")}
            valueTotal={total_drivers_count}
            onMouseDown={(event) => handleDriversCountClick("blocked", team, event, history)}
          />

          <StatItem
            icon="overdues"
            label={t(`stats.late_drivers_count`)}
            value={late_drivers_count}
            valueColor={getStatsColor(teamStats, "late_drivers_count")}
            valueTotal={total_drivers_count}
            onMouseDown={(event) => handleDriversCountClick("late", team, event, history)}
          />
        </div>
      )}

      {notifications && (
        <NotificationsList
          wrapperClassName="hide-mobile"
          className={cls.notificationsList}
          notifications={notifications}
          notificationsStatus={notificationsStatus}
          toggleHiddenSections={toggleHiddenSections}
        />
      )}
    </Main>
  )
}

type StatItemProps = {
  icon: IconName
  label: keyof TeamStats
  value: React.ReactNode
  valueTotal?: number
  valueColor?: ThemeColor
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}

const StatItem = ({ icon, label, value, valueColor, valueTotal, onMouseDown }: StatItemProps) => {
  return (
    <div className={clsx(cls.statItem, onMouseDown && "cursor-pointer")} onMouseDown={onMouseDown}>
      <Icon name={icon} className={cls.icon} />
      <div className={cls.value}>
        <span className={valueColor && `text-${valueColor}`}>{value}</span>
        {typeof valueTotal === "number" ? <span className={cls.valueTotal}>{valueTotal}</span> : null}
      </div>
      <span className={cls.label}>{label}</span>
    </div>
  )
}
